/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: MyLabelAuthFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/11/10 @ 151
 * ---
 * Description:
 */

import {Editor} from '../../../observables/Editor';
import {action, computed, makeObservable, observable} from 'mobx';
import {EditorFeature} from '../../common/EditorFeature';
import {mleDomainGqlClient} from '../../../../api/domain';
import {CREATE_DATAORIGIN, ML_LOGIN} from '../../../../api/domain/mutations.gql';
import {safeRequest} from '../../../../common/hooks';
import {DataOrigin, OriginContentType} from '../../../../entities';
import {GET_DATAORIGINS_BY_COMPANY_ID} from '../../../../api/domain/queries.gql';
import {DataOriginType} from '@revodigital/mle-dataresolver/lib/entities';
import {MLAuthStandaloneInstanceManager, MyLabelAuthStandalone} from "./MLAuthStandalone";

export const ML_AUTH_FNAME = 'ml_auth';

/**
 * Minuti di durata del token di accesso
 */
export const TOKEN_EXP = 15;

/**
 * Esegue una chiamata alle api mylabel autenticandosi prima. Se l'autenticazione fallisce restituisce undefined, altrimenti il risultato della richiesta. Nel caso sia necessario inserire le credenziali, triggera la visualizzazione del dialogo di inserimento.
 * Funge anche da protettore per eventuali eccezioni
 * @param editor Puntatore all'work-area su cui operare
 * @param fun Funzione da eseguire
 */
export const mlAuthSecured = async <T>(editor: Editor, fun: () => Promise<T>): Promise<T | undefined> => {
  // Load credentials
  editor.mylabelAuthFeature.loadFromStorage();
  if (!editor.mylabelAuthFeature.hasCredentials) editor.requiresMLAuth = true;
  else {
    // Perform auth on service
    const result = await editor.mylabelAuthFeature.performAuthOnService();

    if (!result) return undefined;

    let data;

    try {
      data = await fun();
    } catch (e) {
      data = undefined;
    }
    return data;
  }
  return undefined;
};

/**
 * Feature per gestire l'autenticazione alle api mylabel
 */
export class MyLabelAuthFeature extends EditorFeature<void> {
  private _implementation: MyLabelAuthStandalone = new MyLabelAuthStandalone();
  public static __usr_key = 'ml_usr';
  public static __usr_pwd = 'ml_pwd';

  @observable
  private _username: string;
  @observable
  private _password: string;
  @observable
  private _connected: boolean;

  constructor(parent: Editor) {
    super(ML_AUTH_FNAME,
        parent);

    this._password = '';
    this._hasCredentials = false;
    this._username = '';
    this._isLogged = false;
    this._connected = false;

    // Load from storage
    this.loadFromStorage();

    // Make us observable
    makeObservable(this);
  }

  @observable
  private _isLogged: boolean;

  @computed
  public get isLogged() {
    return this._isLogged;
  }

  @computed
  public get username() {
    return this._username;
  }

  @computed
  public get password() {
    return this._password;
  }

  @observable
  private _hasCredentials: boolean;

  @computed
  public get hasCredentials() {
    return this._hasCredentials;
  }

  @action
  public setUsername(value: string) {
    this._username = value;
  }

  @action
  public setPassword(value: string) {
    this._password = value;
  }

  @computed
  public get isConnected() {
    return this._connected;
  }

  @action
  public setIsConnected(value: boolean) {
    this._connected = value;
  }

  /**
   * Controlla se la configurazione a mylabel è completa o manca qualcosa
   * @param companyId
   */
  public async hasCompleteConfiguration(companyId: number): Promise<boolean> {
    // Check credentialis
    this.loadFromStorage();
    if (!this._hasCredentials) return false;

    // Check remote link
    return this.hasRemoteLink(companyId);
  }

  /**
   * Controlla se è presente il link remoto a mylabel
   * @param companyId
   */
  public async hasRemoteLink(companyId: number): Promise<boolean> {
    // Check remote link
    const dataOrigins = await safeRequest<DataOrigin[]>(async () => {
      return (await mleDomainGqlClient.query({
        query: GET_DATAORIGINS_BY_COMPANY_ID,
        variables: {
          id: companyId
        },
        fetchPolicy: 'no-cache'
      })).data.company.dataOrigins;
    });

    if (dataOrigins && !(dataOrigins instanceof Error))
      return (dataOrigins as DataOrigin[]).filter(it => it.contentType === OriginContentType.MyLabel).length > 0;

    return false;
  }

  /**
   * Crea il link remoto per collegare mylabel e completa la configurazione
   * @param companyId
   */
  public async createRemoteLink(companyId: number): Promise<void> {
    await safeRequest(async () => {
      const dataOrigin = await mleDomainGqlClient.mutate({
        mutation: CREATE_DATAORIGIN,
        variables: {
          input: {
            type: DataOriginType.REMOTE,
            hasCredentials: true,
            resource: '',
            name: `MyLabel`,
            contentType: OriginContentType.MyLabel,
            companyId: companyId
          }
        }
      });
    });
  }

  /**
   * Controlla se è connessa una origine dati mylabel a questo profilo
   * @param companyId
   */
  public static async checkConnection(companyId: number) {
    // Fetch dataorigins by company id
    const dataOrigins = await safeRequest<DataOrigin[]>(async () => {
      return (await mleDomainGqlClient.query({
        query: GET_DATAORIGINS_BY_COMPANY_ID,
        variables: {
          id: companyId
        },
        fetchPolicy: 'no-cache'
      })).data.company.dataOrigins;
    });

    // Check if there are errors
    if (dataOrigins && !(dataOrigins instanceof Error)) {
      const dataOrig = dataOrigins as DataOrigin[];
      if (dataOrig.filter(it => it.contentType === OriginContentType.MyLabel).length > 0) return true;
    }

    return false;
  }

  /**
   * Controlla se è necessario il salvataggio delle credenziali o sono già
   * state salvate in locale
   */
  public static requiresCredentials(): boolean {
    return MLAuthStandaloneInstanceManager.getInstance().requiresCredentials();
  }

  /**
   * Rimuove le credenziali salvate dalla localstorage
   */
  public static removeConfiguration() {
    MLAuthStandaloneInstanceManager.getInstance().removeConfiguration();
  }


  /**
   * Effettua l'autenticazione ai serivizi mylabel
   */
  @action
  public async performAuthOnService(): Promise<boolean> {
    if (this._hasCredentials) {
      try {
        await mleDomainGqlClient.mutate({
          mutation: ML_LOGIN,
          variables: {
            username: this._username,
            password: this._password
          }
        });

        this._isLogged = true;

        // The token expires in 15 minutes
        setTimeout(() => {
          this._isLogged = false;
        }, TOKEN_EXP * 3600);

        return true;
      } catch (e) {
        console.error(e);
      }
    }

    return false;
  }

  destroy(): boolean {
    return false;
  }

  init(): boolean {
    this.loadFromStorage();

    return true;
  }

  @action
  public setAuthenticated(usrName: string, password: string) {
    this.setPassword(password);
    this.setUsername(usrName);
    this._hasCredentials = true;

    MLAuthStandaloneInstanceManager.getInstance().writeToStorage(usrName, password);
  }

  /**
   * Carica le credenziali dalla localstorage
   */
  public loadFromStorage() {
    // Load credentials if any
    const key = window.localStorage.getItem(MyLabelAuthStandalone.__usr_key);
    const pwd = window.localStorage.getItem(MyLabelAuthStandalone.__usr_pwd);

    if (key && pwd) {
      this._password = pwd;
      this._username = key;
      this._hasCredentials = true;
      this._isLogged = false;
    } else
      console.warn("No MyLabel credentials saved. ");
  }
}
